import $ from 'jquery'
import { Controller } from 'stimulus'
import { useIntersection } from 'stimulus-use'

export default class extends Controller {
  static values = {
    url: String,
    lazyLoadInfo: Object,
    loadOnConnect: Boolean,
    loadWhenVisible: Boolean,
    replaceSelector: String,
    replacementMode: String,
    retryInModal: Boolean,
    dataType: String,
  }

  static targets = ['spinnerContainer']

  // Later, we might want to introduce more options for when to load
  // E.g Load after a delay
  connect() {
    if (this.loadOnConnectValue) {
      this.loadContent()
    }

    if (this.loadWhenVisibleValue) {
      const [observe, unobserve] = useIntersection(this)
      this.observe = observe
      this.unobserve = unobserve
    }
  }

  appear(_entry, _observer) {
    this.loadContent()
    this.unobserve()
  }

  loadContent() {
    if (this.loadContentCalled) return
    this.loadContentCalled = true

    $.ajax({
      method: this.hasLazyLoadInfoValue ? 'POST' : 'GET',
      url: this.urlValue,
      data: this.hasLazyLoadInfoValue ? this.lazyLoadInfoValue : undefined,
      dataType: this.dataTypeValue,
      global: false,
    })
      .done((htmlOrJson) => {
        const html =
          this.dataTypeValue === 'json' ? htmlOrJson.lazy_content : htmlOrJson
        this.spinnerContainerTarget.remove()

        const $html = $(html).not('comment')

        /*
         * If we want to replace by a particular selector, find that selector in the response and replace it
         */
        if (this.replaceSelectorValue) {
          this.performReplacement(
            $(this.element),
            $html.find(this.replaceSelectorValue)
          )
          return
        }

        /*
         * If the response html has a single html element that has an id set, we look for that ID in the dom.
         * If we find such an element, we replace that element with this new one.
         * Otherwise, we append the html of the response to this component's container
         */
        const idToReplace = $html.length === 1 && $html.attr('id')
        const elToReplace =
          idToReplace &&
          (this.element.closest(`#${idToReplace}`) ||
            document.getElementById(idToReplace))

        if (elToReplace) {
          this.performReplacement($(elToReplace), $html, 'replace')
        } else {
          this.performReplacement($(this.element), $html)
        }
      })
      .fail((_, status, error) => {
        console.error(
          'An error occurred while lazily loading the content:',
          status,
          error
        )
        // Closes the surrounding modal and keeps the spinner, so opening it again retries (reloadIn)
        if (this.retryInModalValue) {
          this.loadContentCalled = false
          this.element
            .closest('.js-modal')
            ?.dispatchEvent(new CustomEvent('modal:hide'))
          return
        }
        this.spinnerContainerTarget.remove()
      })
  }

  // Retries a failed load when the dialog around this content opens again (modal:shown@window)
  reloadIn(event) {
    if (event.target.contains(this.element)) this.loadContent()
  }

  performReplacement($element, $replacement, replacementMode = null) {
    replacementMode = replacementMode || this.replacementModeValue
    if (replacementMode === 'replace') {
      $element.replaceWith($replacement)
      $replacement.changed()
    } else if (replacementMode === 'append') {
      $element.append($replacement).changed()
    }

    if ($replacement[0]) this.dispatch('loaded', { target: $replacement[0] })

    this.scrollToAnchor($replacement)
  }

  scrollToAnchor($content) {
    const hash = window.location.hash?.slice(1)
    if (!hash) return

    const target = $content
      .find(`#${CSS.escape(hash)}`)
      .addBack(`#${CSS.escape(hash)}`)[0]

    if (target) {
      // Artificial delay cause, things might need time to initialize and cause layout shift after we're done scrolling!
      setTimeout(() => {
        requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      }, 100)
    }
  }
}
